import { h, render } from 'preact'
import { useState, useRef, useEffect, useCallback } from 'preact/hooks'
import { createPortal } from 'preact/compat'

// Animation duration in ms (should match CSS transition times)
const ENTER_ANIMATION_DURATION = 100
const EXIT_ANIMATION_DURATION = 350

interface ModalDialogProps {
  isOpen: boolean
  onClose: () => void
  closeSlot: HTMLElement | null
  contentSlot: HTMLElement | null
}

function ModalDialog({
  isOpen,
  onClose,
  closeSlot,
  contentSlot,
}: ModalDialogProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const closeContainerRef = useRef<HTMLDivElement>(null)
  const contentContainerRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Track render state: 'hidden' | 'entering' | 'open' | 'exiting'
  const [renderState, setRenderState] = useState<
    'hidden' | 'entering' | 'open' | 'exiting'
  >('hidden')

  // Handle open transition
  useEffect(() => {
    if (isOpen && renderState === 'hidden') {
      // Start entering - mount the element first
      setRenderState('entering')
    } else if (
      !isOpen &&
      (renderState === 'open' || renderState === 'entering')
    ) {
      // Start exiting
      setRenderState('exiting')
    }
  }, [isOpen, renderState])

  // Handle entering -> open transition (after mount, trigger CSS animation)
  useEffect(() => {
    if (renderState === 'entering') {
      // Double RAF to ensure DOM is painted before adding is-open class
      const raf1 = requestAnimationFrame(() => {
        const raf2 = requestAnimationFrame(() => {
          setRenderState('open')
        })
        return () => cancelAnimationFrame(raf2)
      })
      return () => cancelAnimationFrame(raf1)
    }
  }, [renderState])

  // Handle exiting -> hidden transition (after animation completes)
  useEffect(() => {
    if (renderState === 'exiting') {
      const timer = setTimeout(() => {
        setRenderState('hidden')
      }, EXIT_ANIMATION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [renderState])

  // Move slotted elements into the modal when visible
  useEffect(() => {
    const isVisible = renderState !== 'hidden'

    if (closeSlot) {
      if (isVisible && closeContainerRef.current) {
        closeSlot.style.display = ''
        closeContainerRef.current.appendChild(closeSlot)
      } else if (!isVisible) {
        closeSlot.style.display = 'none'
      }
    }
    if (contentSlot) {
      if (isVisible && contentContainerRef.current) {
        contentSlot.style.display = ''
        contentContainerRef.current.appendChild(contentSlot)
      } else if (!isVisible) {
        contentSlot.style.display = 'none'
      }
    }
  }, [renderState, closeSlot, contentSlot])

  // Handle close button click
  useEffect(() => {
    if (closeSlot && renderState === 'open') {
      const handleClose = (e: Event) => {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
      closeSlot.addEventListener('click', handleClose)
      return () => closeSlot.removeEventListener('click', handleClose)
    }
  }, [closeSlot, renderState, onClose])

  // Handle escape key
  useEffect(() => {
    if (renderState === 'open') {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [renderState, onClose])

  // Focus management and body scroll lock
  useEffect(() => {
    if (renderState === 'open') {
      // Store the currently focused element
      previousActiveElement.current = document.activeElement as HTMLElement

      // Lock body scroll
      document.body.style.overflow = 'hidden'

      // Make everything else inert (except the portal container)
      document
        .querySelectorAll('body > *:not(.x-profile-modal-portal)')
        .forEach((el) => {
          if (el instanceof HTMLElement) {
            el.setAttribute('inert', '')
            el.setAttribute('aria-hidden', 'true')
          }
        })

      // Focus the modal panel
      setTimeout(() => {
        if (panelRef.current) {
          panelRef.current.focus()
        }
      }, ENTER_ANIMATION_DURATION)
    }

    return () => {
      if (renderState === 'open') {
        document.body.style.overflow = ''
        document.querySelectorAll('body > *[inert]').forEach((el) => {
          if (el instanceof HTMLElement) {
            el.removeAttribute('inert')
            el.removeAttribute('aria-hidden')
          }
        })

        // Restore focus to the trigger element
        if (previousActiveElement.current) {
          previousActiveElement.current.focus()
        }
      }
    }
  }, [renderState])

  // Trap focus within modal
  useEffect(() => {
    if (renderState !== 'open' || !panelRef.current) return

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusableElements = panelRef.current!.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )

      if (focusableElements.length === 0) return

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
    }

    document.addEventListener('keydown', handleTabKey)
    return () => document.removeEventListener('keydown', handleTabKey)
  }, [renderState])

  // Don't render if hidden
  if (renderState === 'hidden') return null

  // Determine if the is-open class should be applied
  const showOpenClass = renderState === 'open'

  // Render the modal to a portal at the body level
  return createPortal(
    <div
      ref={overlayRef}
      className={`team-modal-overlay ${showOpenClass ? 'is-open' : ''}`}>
      <div
        className="team-modal-backdrop"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="team-modal-container">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          className="team-modal-panel">
          {/* Close button slot */}
          <div
            ref={closeContainerRef}
            className="absolute top-16 right-16 z-10"
          />

          {/* Content slot */}
          <div ref={contentContainerRef} className="team-modal-content" />
        </div>
      </div>
    </div>,
    getPortalContainer(),
  )
}

// Get or create a portal container at the body level
function getPortalContainer(): HTMLElement {
  let container = document.querySelector('.x-profile-modal-portal')
  if (!container) {
    container = document.createElement('div')
    container.className = 'x-profile-modal-portal'
    document.body.appendChild(container)
  }
  return container as HTMLElement
}

interface ProfileModalComponentProps {
  triggerSlot: HTMLElement | null
  closeSlot: HTMLElement | null
  contentSlot: HTMLElement | null
}

function ProfileModalComponent({
  triggerSlot,
  closeSlot,
  contentSlot,
}: ProfileModalComponentProps) {
  const [isOpen, setIsOpen] = useState(false)
  const triggerContainerRef = useRef<HTMLDivElement>(null)

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handleOpen = useCallback(() => {
    setIsOpen(true)
  }, [])

  // Move trigger slot into container
  useEffect(() => {
    if (triggerContainerRef.current && triggerSlot) {
      triggerContainerRef.current.appendChild(triggerSlot)
    }
  }, [triggerSlot])

  // Attach trigger click handler
  useEffect(() => {
    if (triggerSlot) {
      triggerSlot.addEventListener('click', handleOpen)

      // Add keyboard support
      triggerSlot.setAttribute('tabindex', '0')
      triggerSlot.setAttribute('role', 'button')

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          setIsOpen(true)
        }
      }
      triggerSlot.addEventListener('keydown', handleKeyDown)

      return () => {
        triggerSlot.removeEventListener('click', handleOpen)
        triggerSlot.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [triggerSlot, handleOpen])

  return (
    <>
      {/* Trigger slot container */}
      <div ref={triggerContainerRef} style={{ display: 'contents' }} />

      <ModalDialog
        isOpen={isOpen}
        onClose={handleClose}
        closeSlot={closeSlot}
        contentSlot={contentSlot}
      />
    </>
  )
}

// Web Component definition
class XProfileModal extends HTMLElement {
  private _mounted = false
  private _triggerSlot: HTMLElement | null = null
  private _closeSlot: HTMLElement | null = null
  private _contentSlot: HTMLElement | null = null
  private _renderRoot: HTMLDivElement | null = null

  connectedCallback() {
    if (this._mounted) return
    this._mounted = true

    // Extract slotted elements before rendering
    this._triggerSlot = this.querySelector('[slot="trigger"]')
    this._closeSlot = this.querySelector('[slot="close"]')
    this._contentSlot = this.querySelector('[slot="content"]')

    // Remove slot attributes so they don't interfere
    this._triggerSlot?.removeAttribute('slot')
    this._closeSlot?.removeAttribute('slot')
    this._contentSlot?.removeAttribute('slot')

    // Hide close and content slots initially
    if (this._closeSlot) {
      this._closeSlot.style.display = 'none'
    }
    if (this._contentSlot) {
      this._contentSlot.style.display = 'none'
    }

    // Create a render root
    this._renderRoot = document.createElement('div')
    this._renderRoot.style.display = 'contents'
    this.appendChild(this._renderRoot)

    this._render()
  }

  disconnectedCallback() {
    if (this._renderRoot) {
      render(null, this._renderRoot)
    }
    this._mounted = false
  }

  private _render() {
    if (!this._renderRoot) return

    render(
      <ProfileModalComponent
        triggerSlot={this._triggerSlot}
        closeSlot={this._closeSlot}
        contentSlot={this._contentSlot}
      />,
      this._renderRoot,
    )
  }
}

// Register the custom element
if (!customElements.get('x-profile-modal')) {
  customElements.define('x-profile-modal', XProfileModal)
}

export default XProfileModal
